import type { ClientConfig } from '../types';

/**
 * DF.
 *
 * The palette below is DF's own dark theme; the feature set still starts from
 * 99h's, so switch off anything DF should not ship.
 */
export const clientDf: ClientConfig = {
  id: 'df',
  // Shown in the PWA manifest and the app header.
  name: 'DF',
  nameByLocale: {
    zh: '东风国际',
    // Traditional rendering of the same name, for the zh-tw build.
    'zh-tw': '東風國際',
  },

  theme: {
    app: '#000000',
    surface: '#000000',
    text: '#FFFFFF',
    muted: '#888888',
    // Card blurbs and the notice bar sit in DF's grey rather than following
    // the accent — the same grey as `muted` and as the bottom-nav artwork.
    'text-secondary': 'var(--color-muted)',
    // DF has one highlight colour, not two: the sign-in / sign-up CTA and the
    // "Sign Up" / "Sign In" links in the mock are the same green as the nav
    // and the refresh button, so the accent follows `brand` instead of
    // carrying 99h's blue.
    accent: 'var(--color-brand)',
    'accent-text': '#ffffff',
    link: 'var(--color-brand)',
    danger: '#ff3b30',
    brand: '#00B24A',
    'brand-soft': '#f2f2f3',
    // Dividers inside cards, not a light-theme hairline on a black page.
    line: '#2A2B2C',
    // #171819 is DF's single raised surface: every card, input, chip, pill
    // and tab slab on the black page is this one value, so nothing reads as
    // a different kind of panel. The two values a step above it — `line`,
    // `track` / `skeleton-shine` and `status-pending` — exist only to show
    // up *on* that surface, never directly on the page.
    card: '#171819',
    'card-border': '#171819',
    panel: '#171819',
    // The header balance pill is the same slab, with a solid brand refresh
    // button, so the button needs no hairline of its own.
    pill: 'var(--color-sheet)',
    'pill-action': 'var(--color-brand)',
    'pill-action-border': 'transparent',
    'pill-action-text': '#ffffff',
    // Login sits beside Register as a filled dark pill, not an outline.
    'header-btn': '#171819',
    'header-btn-border': 'transparent',
    // A track one step lighter so the VIP progress bar reads against the
    // card. No wash behind the top of the Mine page — DF's is flat black.
    sheet: '#171819',
    'sheet-contrast': '#ffffff',
    'sheet-contrast-text': '#111111',
    // One step above the #171819 slab. The page is darker than the cards
    // here, so a value field cannot borrow the page tone the way a light
    // client does — it has to come up, not down.
    field: '#212324',
    // Nothing outlines a card here — the slab's own colour is the edge, so
    // the hairline resolves to the card and vanishes.
    'sheet-border': 'var(--color-sheet)',
    // A table's header and its zebra band step UP from the slab rather than
    // down. The header lands on the same step as `line`, the stripe sits
    // between the two so the alternation reads without striping the card.
    'table-head': '#2A2B2C',
    'table-stripe': '#202122',
    track: '#2A2B2C',
    'page-glow': 'none',
    // Chips are a tinted slab rather than a pastel one, so they read against
    // the black page instead of glowing off it.
    positive: 'var(--color-brand)',
    'status-pending': '#2A2B2C',
    'status-pending-text': 'var(--color-muted)',
    'status-failed': '#3A1A1C',
    'status-failed-text': '#FF4D4F',
    'status-success': '#12301F',
    'status-success-text': 'var(--color-brand)',
    'option-active': '#171819',
    'warn-soft': '#171819',
    // One green across the client, and a red that reads on black.
    gain: 'var(--color-positive)',
    loss: '#EE2C2C',
    // A dark slab on the black page, with the selected segment filled in
    // brand and labelled white. The band goes solid rather than blurred.
    tabbar: '#171819',
    'tabbar-band': 'var(--color-app)',
    'tabbar-active': 'var(--color-brand)',
    'tabbar-active-text': '#ffffff',
    // Matches the #888888 baked into DF's own nav artwork.
    'nav-inactive': 'var(--color-muted)',
    // Rule along the top of the bottom nav.
    'nav-border': '#171819',
    // No bump — the Deposit button floats over the page instead of sitting
    // in a filled half-circle.
    'nav-bump': 'transparent',
    // Tab strip and "All" chip: raised panels on the black page, with the
    // resting labels in DF's grey.
    'tab-active': '#171819',
    'tab-inactive': 'var(--color-muted)',
    // Inputs and buttons lift off the black page rather than vanishing into it.
    control: '#171819',
    'control-border': '#171819',
    disabled: '#171819',
    chip: '#171819',
    'chip-text': 'var(--color-muted)',
    'chip-border': '#171819',
    // The shimmer is what moves; the base matches the card it replaces.
    skeleton: '#171819',
    'skeleton-shine': '#2A2B2C',
  },

  assets: {
    // TODO(df): point at DF's own bucket once its artwork is uploaded.
    cdn: 'https://pub-dbd64b2aa45940c2ba68ae732da32aa0.r2.dev',

    logo: '/home/icons/logo.png',

    // DF's own category-card artwork, same six filenames as the shared set.
    categoryArt: 'https://pub-dbd64b2aa45940c2ba68ae732da32aa0.r2.dev/DF',
    cardPointer: '/home/icons/titlePointer.png',

    // DF's own copy glyph, drawn everywhere the shared one was. Not under
    // `Mine/` or `wallet/` like the two files it replaces — it answers for
    // both, so it belongs to neither.
    copyIcon: '/icons/copy.png',

    // The browser tab. Reuses the 180px home-screen icon rather than adding
    // a second file — browsers downscale it, and one file cannot drift from
    // the other.
    favicon: '/pwa/icons/img_icon_180.png',

    // DF's bottom nav. The four side tabs are single-colour silhouettes, so
    // they are masked and tinted like the built-in SVGs — one file covers
    // both the active and inactive states. The deposit button is full-colour
    // artwork and is drawn as-is.
    icons: {
      'nav-home': { src: '/nav/home.png', mask: true },
      'nav-game': { src: '/nav/game.png', mask: true },
      'nav-activity': { src: '/nav/activity.png', mask: true },
      'nav-mine': { src: '/nav/mine.png', mask: true },
      // Full-colour artwork, drawn at DF's own 30x30 rather than the 54 the
      // built-in FAB uses.
      // 8px below the bump's centre line — 1.5rem from the nav's top edge.
      'nav-deposit': { src: '/nav/deposit.png', size: 30, offsetY: 8 },

      // Game-page tab glyphs. Single-colour silhouettes like the nav icons,
      // so they mask and take the active tab's brand colour.
      'game-tab-live-casino': { src: '/game/tabs/liveCasino.png', mask: true },
      'game-tab-slots': { src: '/game/tabs/slots.png', mask: true },
      'game-tab-hash': { src: '/game/tabs/hashGame.png', mask: true },
      'game-tab-sports': { src: '/game/tabs/sports.png', mask: true },
      'game-tab-card': { src: '/game/tabs/card.png', mask: true },
      'game-tab-fishing': { src: '/game/tabs/fishing.png', mask: true },
      'game-tab-lottery': { src: '/game/tabs/lottery.png', mask: true },
    },
  },

  // DF leads with Sports in the tall slot, Live Casino and Lottery stacked
  // beside it, and runs the bottom three as title-only cards.
  home: {
    // One fixed banner rather than the backend's slide carousel — DF's
    // artwork is not managed in the CMS, so the endpoint is never called.
    banner: 'https://pub-dbd64b2aa45940c2ba68ae732da32aa0.r2.dev/DF/dfBanner.png',
    featured: ['sports', 'live', 'lottery'],
    heroAlign: 'left',
    // 6px against the 360px baseline, so the artwork sits just inside the
    // card rather than bleeding to its edges.
    heroPadX: 'px-[0.375rem]',
    // 8px between the artwork and the caption.
    heroGap: 'gap-[0.5rem]',
    // 12px either side of the title and blurb.
    heroCaptionPadX: 'px-[0.45rem]',
    // 10px / 12px line-height, against the 360px baseline.
    descClass: 'text-[0.625rem] font-normal leading-[0.75rem]',
    bottomRowDesc: false,
    bottomRowPointer: false,
    // 10px between the artwork and the title.
    bottomRowGap: 'gap-[0.625rem]',
    copy: {
      sports: { titleKey: 'cat_sports_alt', descKey: 'cat_sports_alt_desc' },
      live: { descKey: 'cat_live_alt_desc' },
    },
    // DF's live-casino art is tight-cropped, so the shared box lets it run
    // under the caption. Narrowed to clear the copy beside it.
    art: {
      live: 'h-[4.8125rem] w-[5rem] right-[-4px]',
      // Pushed further off the right edge than the shared default.
      lottery: 'h-[5.8125rem] w-[6.1625rem] right-[-13px]',
    },
    // The stacked titles sit 16px down from the card's top edge rather than
    // riding over it, which is where the shared cards put them. Against the
    // 360px baseline, like every other measurement here.
    stackedCaptionOffset: 'pt-[1rem]',
    // Both sit higher off their floor than the shared 4px; the title-only
    // bottom row needs the most. 12px and 20px against the 360px baseline.
    captionPad: { hero: 'pb-[0.75rem]', bottomRow: 'pb-[1.25rem]' },
  },

  // DF carries no fishing or card games yet; both tabs stay in the strip and
  // show the empty state.
  // DF funds accounts through a finance contact rather than on-chain.
  // TODO(df): `account` is a placeholder — swap for the backend's finance
  // account once that endpoint exists.
  wallet: {
    mode: 'agent',
    channels: [
      { id: 'wechat', label: 'Wechat', icon: '/wallet/icons/wechat.png', account: '@DFGJCW' },
      { id: 'alipay', label: 'AliPay', icon: '/wallet/icons/alipay.png', account: '@DFGJCW' },
    ],
  },

  // The agent wallet has no on-chain address and no payment password, and
  // account binding happens through the finance contact.
  settings: { hiddenRows: ['binding_account', 'payment_password', 'wallet_address'] },

  // The header pill already owns the balance refresh, and the mock's Sign
  // Out is a bare label.
  mine: { cardRefresh: false, signOutIcon: false },

  // DF runs no campaigns yet, so the Activity page is the banner alone and
  // the activities endpoint is never called.
  activity: {
    banner: 'https://pub-dbd64b2aa45940c2ba68ae732da32aa0.r2.dev/DF/dfBanner.png',
  },

  game: {
    emptyTabs: ['fishing', 'card'],
  },

  // Starts with everything on, matching 99h. Turning a flag off removes that
  // capability from the DF build only.
  features: {
    originalGames: true,
    // No agent tree in the mock's Mine list.
    referralProgram: false,
    telegramAuth: true,
    // DF's login screens are account + password only.
    tgQuickSignIn: false,
    rememberPassword: false,
    pwaBanner: false,
    liveChat: true,
    // DF's Home has no scrolling announcement bar.
    noticeMarquee: false,
    // DF runs no operator announcements, so nothing pops up over Home or
    // over a fresh sign-in, and neither notification endpoint is called.
    notifications: false,
    followTelegramTheme: false,
    accountInHeader: true,
  },
};
